import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import { useTranslation } from "@shared/utils/internationalization";
import { Dispatch, SetStateAction } from "react";
import { enums } from "@shared/constants";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import IconButton from "@shared/components/ui-kit/form/IconButton/IconButton.web";

export interface CartQuantityButtonsProps {
  minSelectionAmount?: number;
  maxSelectionAmount?: number;
  amountSelected: number;
  setAmountSelected: Dispatch<SetStateAction<{ amount: number }>>;
  addItem: () => void;
  removeItem: () => void;
  item?: TicketItemGroupType;
  disabled: boolean;
}

const CartQuantityButtons: React.FC<CartQuantityButtonsProps> = ({
  minSelectionAmount,
  maxSelectionAmount,
  amountSelected,
  setAmountSelected,
  addItem,
  removeItem,
  item,
  disabled,
}) => {
  const { t } = useTranslation("purchases");

  return (
    <Flex
      border="1px solid"
      borderColor="gray.400"
      borderRadius="sm"
      height="element.2xl"
      justifyContent="space-between"
      alignItems="center"
      data-testid="cart-quantity-buttons"
    >
      <IconButton
        borderRadius="inherit"
        minWidth="element.md"
        width="element.lg"
        height="100%"
        hoverColor="white"
        _hover={{
          color: "white",
          backgroundColor: "primary.500",
        }}
        _disabled={{
          color: "gray.300",
          backgroundColor: "font.light",
        }}
        sx={{
          _disabled: {
            _hover: {
              backgroundColor: "font.light",
            },
          },
        }}
        iconName="Minus"
        iconColor="black"
        disabled={disabled || amountSelected <= minSelectionAmount}
        aria-label={t("Remove item")}
        onClick={() => {
          if (
            item.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id ||
            item.info_required
          ) {
            removeItem();
            return;
          }

          if (minSelectionAmount && amountSelected === minSelectionAmount) {
            setAmountSelected({ amount: 0 });
            removeItem?.(); // TODO: this function needs to handle regular selection and minimum selection
            return;
          }
          setAmountSelected({ amount: amountSelected - 1 });
          removeItem?.();
        }}
      />
      <Flex
        justifyContent="center"
        fontSize="xs"
        fontWeight="bold"
        width="element.xl"
      >
        {amountSelected}
      </Flex>
      <IconButton
        borderRadius="inherit"
        minWidth="element.md"
        width="element.lg"
        height="100%"
        hoverColor="white"
        _hover={{
          color: "white",
          backgroundColor: "primary.500",
        }}
        _disabled={{
          color: "gray.300",
          backgroundColor: "font.light",
        }}
        sx={{
          _disabled: {
            _hover: {
              backgroundColor: "font.light",
            },
          },
        }}
        iconName="Plus"
        iconColor="black"
        disabled={disabled || amountSelected >= maxSelectionAmount}
        aria-label={t("Add item")}
        onClick={() => {
          if (
            item.item_type === enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id ||
            item.info_required
          ) {
            addItem();
            return;
          }

          if (minSelectionAmount && amountSelected === 0) {
            setAmountSelected({ amount: minSelectionAmount });
            addItem?.(); // TODO: this function needs to handle regular selection and minimum selection
            return;
          }
          setAmountSelected({ amount: amountSelected + 1 });
          addItem?.();
        }}
      />
    </Flex>
  );
};

export default CartQuantityButtons;
